import {action, makeAutoObservable} from "mobx";
import CommonStore from "../../../stores/CommonStore";
import DeliveryPerson from "../../../entities/DeliveryPerson";
import {DeliveryPersonService} from "../../../service/DeliveryPersonService";
import TransferArticleRequest from "../../../model/transaction/TransferArticleRequest";
import ArticleStockResponse from "../../../model/articleDelivery/ArticleStockResponse";
import ArticleStockRequest from "../../../model/transaction/ArticleStockRequest";

export interface TransferArticleItem {
    id: string; // ID único para o item na lista
    article?: ArticleStockResponse;
    amount?: number;
    articleError?: string;
    amountError?: string;
}

class TransferArticleStore {

    public articles: TransferArticleItem[] = [];
    public comment?: string;
    public from?: DeliveryPerson;
    public to?: DeliveryPerson;
    public isModalOpen: boolean;
    public loading: boolean;
    public commonStore: CommonStore;
    public fromError?: string;
    public toError?: string;
    public error?: string;


    constructor(commonStore: CommonStore, selectedFrom?: DeliveryPerson, selectedTo?: DeliveryPerson) {
        this.isModalOpen = false;
        this.loading = false;
        this.commonStore = commonStore;
        this.from = selectedFrom;
        this.to = selectedTo;
        this.articles = [];
        makeAutoObservable(this);
    }

    get enableButton(): boolean {
        return !!(this.from && this.to && this.articles.length > 0 && this.articles.every(item => item.article && item.amount && item.amount !== 0));
    }

    @action
    public addArticleItem() {
        const newItem: TransferArticleItem = {
            id: `${Date.now()}-${Math.random()}`,
            article: undefined,
            amount: undefined,
            articleError: undefined,
            amountError: undefined
        };
        this.articles.push(newItem);
    }

    @action
    public removeArticleItem(id: string) {
        this.articles = this.articles.filter(item => item.id !== id);
    }

    @action
    public updateArticleItem(id: string, article?: ArticleStockResponse) {
        const item = this.articles.find(i => i.id === id);
        if (item) {
            item.article = article;
            item.articleError = undefined;
        }
    }

    @action
    public updateAmountItem(id: string, amount: number) {
        const item = this.articles.find(i => i.id === id);
        if (item) {
            item.amount = amount;
            item.amountError = undefined;
        }
    }

    @action
    public async openModal() {
        this.loading = true;
        this.isModalOpen = true;
        if (this.articles.length === 0) {
            this.addArticleItem();
        }
        this.loading = false;
    }

    @action
    public closeModal() {
        this.isModalOpen = false;
    }

    @action
    public async cleanModal() {
        this.articles = [];
        this.comment = undefined;
        this.to = undefined;
        this.error = undefined;
        this.fromError = undefined;
        this.toError = undefined;
    }


    @action
    public async save(): Promise<boolean> {
        // Validar campos fixos
        if (!this.from) {
            this.fromError = "FROM_REQUIRED";
            return false;
        }
        if (!this.to) {
            this.toError = "TO_REQUIRED";
            return false;
        }

        // Validar artigos
        let hasErrors = false;
        for (const item of this.articles) {
            item.articleError = undefined;
            item.amountError = undefined;

            if (!item.article) {
                item.articleError = "ARTICLE_REQUIRED";
                hasErrors = true;
            }
            if (!item.amount || item.amount === 0) {
                item.amountError = "AMOUNT_REQUIRED";
                hasErrors = true;
            }
        }

        if (this.articles.length === 0) {
            this.error = "ARTICLES_REQUIRED";
            return false;
        }

        if (hasErrors) {
            return false;
        }

        // Salvar todos os artigos
        this.loading = true;
        try {
            const articles = this.articles
                .filter(article => article && article.article && article.amount && article.amount !== 0)
                .map(article => {
                    return new ArticleStockRequest(
                        article.article!!.articleId,
                        article.amount!!
                    )
                })
            const request: TransferArticleRequest = {
                to: this.to.id,
                articles,
                comment: this.comment
            };
            const response = await DeliveryPersonService.transferArticle(this.from.id, request);
            this.commonStore.processErrors(response);
            this.loading = false;
            return response.success;
        } catch (error) {
            this.loading = false;
            this.error = "TRANSFER_ERROR";
            return false;
        }
    }
}

export default TransferArticleStore;
